import {
  Box,
  Flex,
  Text,
  useDisclosure,
  Divider,
  Button,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import React, { useState, useEffect, useMemo } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { DataLake, Util } from '@finley-technologies/finley-types';

import { AthenaRecurringDatesContainer } from './AthenaRecurringDatesContainer';
import { BindingContainer } from './BindingContainer';
import { LenderAthenaQueryPreviewDrawer } from './LenderAthenaQueryPreviewDrawer';
import { AthenaQueryStringWithSchemaSidebarContainer } from 'packages/athena/ui/AthenaQueryStringWithSchemaSidebarContainer';
import { colors } from 'packages/common-frontend/colors';
import {
  DeleteIconButton,
  CopyIconButton,
} from 'packages/common-frontend/components/IconButton';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { DeleteConfirmationModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { QueryInputTypeBadge } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/covenantPostgresQuery/lenderView/QueryInputTypeBadge';
import { WipConfig } from 'packages/creditAgreements/ui/types';
import { AddQueryInputModal } from 'packages/settings/ui/AddQueryInputModal';

type LenderAthenaRecurringQueryViewProps = {
  recurringQuery: CreditAgreement.Config.AthenaConfig.RecurringQuery;
  globalRecurringDateConfigs: CreditAgreement.Config.GlobalRecurringDate.Config[];
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  presetVariables: CreditAgreement.Config.QueryVariable[];
  validationVariables: CreditAgreement.Config.QueryVariable[];
  inputDateType: 'reportingDate' | 'triggerDate';
  selectedInputDate?: Util.FullDateStr;
  isQueryStringSaveLoading: boolean;
  duplicateParams: {
    isDuplicateQueryOpen: boolean;
    onDuplicateQueryOpen: () => void;
    onDuplicateQueryClose: () => void;
    duplicateQuery: (params: { queryKeyInput: string }) => Promise<void>;
    isDuplicateLoading: boolean;
  };
  deleteParams: {
    isDeleteConfirmationModalOpen: boolean;
    onDeleteConfirmationModalOpen: () => void;
    onDeleteConfirmationModalClose: () => void;
    deleteQuery: () => Promise<void>;
    isDeleteLoading: boolean;
  };
  changeQueryString: (params: {
    newQueryString: string;
    queryInputDateForColumnUpdate: Util.FullDateStr;
  }) => Promise<void>;
  queryDataItems: DataLake.AthenaQueryItemData[];
  isPreviewQueryResultsDrawerOpen: boolean;
  onClosePreviewQueryResultsDrawer: () => void;
};
export const LenderAthenaRecurringQueryView = ({
  recurringQuery,
  globalRecurringDateConfigs,
  wipConfig,
  fullPropertyKey,
  presetVariables,
  validationVariables,
  inputDateType,
  selectedInputDate,
  isQueryStringSaveLoading,
  duplicateParams,
  deleteParams,
  changeQueryString,
  queryDataItems,
  isPreviewQueryResultsDrawerOpen,
  onClosePreviewQueryResultsDrawer,
}: LenderAthenaRecurringQueryViewProps) => {
  const [queryStringState, setQueryStringState] = useState<string>(
    recurringQuery.queryString,
  );

  useEffect(() => {
    setQueryStringState(recurringQuery.queryString);
  }, [fullPropertyKey, setQueryStringState, recurringQuery]);

  const {
    isDuplicateQueryOpen,
    onDuplicateQueryOpen,
    onDuplicateQueryClose,
    duplicateQuery,
    isDuplicateLoading,
  } = duplicateParams;
  const {
    isDeleteConfirmationModalOpen,
    onDeleteConfirmationModalOpen,
    onDeleteConfirmationModalClose,
    deleteQuery,
    isDeleteLoading,
  } = deleteParams;
  const queryStringEditModeDisclosure = useDisclosure();

  const inputDate = useMemo(
    () => selectedInputDate ?? commonDate.currentDateStr(),
    [selectedInputDate],
  );

  const bindingsAlert = useMemo(() => {
    const { bindings } = recurringQuery;
    const bindingsInQueryString =
      recurringQuery.queryString.match(/\?/g)?.length ?? 0;

    const bindingsConfigured = bindings.length;
    const doesBindingsLengthMatchQuery =
      bindingsInQueryString === bindings.length;

    const bindingsAlertString = `There ${
      bindingsConfigured === 1 ? 'is' : 'are'
    } ${bindingsConfigured} binding${
      bindingsConfigured === 1 ? '' : 's'
    } configured and ${bindingsInQueryString} binding${
      bindingsInQueryString === 1 ? '' : 's'
    } referenced in the latest saved SQL query. Ensure that all configured bindings are referenced in the SQL query before making further updates.`;

    return { bindingsAlertString, doesBindingsLengthMatchQuery };
  }, [recurringQuery]);

  return (
    <Box>
      <Flex alignItems="center" mb="24px" justifyContent="right">
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <>
            <CopyIconButton
              ariaLabel="duplicate query"
              label="Duplicate Query"
              onClick={() => onDuplicateQueryOpen()}
            />
            <DeleteIconButton
              ariaLabel="delete query"
              label="Delete Query"
              onClick={() => onDeleteConfirmationModalOpen()}
            />
            <AddQueryInputModal
              wipConfig={wipConfig}
              isOpen={isDuplicateQueryOpen}
              onClose={onDuplicateQueryClose}
              onClickAdd={duplicateQuery}
              initialQueryType="multiQuery"
              isAddLoading={isDuplicateLoading}
              mode="duplicate"
            />
            <DeleteConfirmationModal
              isOpen={isDeleteConfirmationModalOpen}
              onClose={onDeleteConfirmationModalClose}
              onClickDelete={deleteQuery}
              isLoading={isDeleteLoading}
            />
          </>
        )}
      </Flex>
      <Divider />
      <Flex
        alignItems="center"
        justifyContent="space-between"
        mt="64px"
        mb="24px"
      >
        <Text fontWeight={600} fontSize="20px">
          Key
        </Text>
        <QueryInputTypeBadge type="multiQuery" />
      </Flex>
      <Text
        color={colors.black[70]}
        p="32px"
        borderWidth="1px"
        borderRadius="8px"
        borderColor={colors.gray[200]}
      >
        {recurringQuery.name}
      </Text>

      {/* *** Recurring Dates Management *** */}
      <Text fontWeight={600} fontSize="20px" mt="64px" mb="24px">
        Recurring Dates
      </Text>
      <AthenaRecurringDatesContainer
        globalRecurringDateConfigs={globalRecurringDateConfigs}
        recurringQuery={recurringQuery}
        fullPropertyKey={fullPropertyKey}
        wipConfig={wipConfig}
      />
      <Box mt="64px">
        {/* *** SQL Query Management *** */}
        <AthenaQueryStringWithSchemaSidebarContainer
          wipConfig={wipConfig}
          fullPropertyKey={`${fullPropertyKey}.queryString`}
          changeQueryString={changeQueryString}
          isQueryStringSaveLoading={isQueryStringSaveLoading}
          queryStringState={queryStringState}
          setQueryStringState={setQueryStringState}
          selectedInputDate={selectedInputDate}
          initialQueryString={recurringQuery.queryString}
          editModeDisclosure={queryStringEditModeDisclosure}
        />

        {/* *** Bindings Management *** */}
        {!bindingsAlert.doesBindingsLengthMatchQuery && (
          <Alert status="error" mt="32px" mb="16px">
            <AlertIcon />
            {bindingsAlert.bindingsAlertString}
          </Alert>
        )}
        <BindingContainer
          wipConfig={wipConfig}
          bindings={recurringQuery.bindings}
          bindingsPropertyKey={`${fullPropertyKey}.bindings`}
          presetVariables={presetVariables}
          validationVariables={validationVariables}
          query={recurringQuery}
          queryType="multiQuery"
          globalRecurringDateConfigs={globalRecurringDateConfigs}
        />

        {/* *** Preview and Save Query *** */}
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <Flex
            alignItems="center"
            width="100%"
            justifyContent="flex-end"
            mt="64px"
          >
            <Button
              color="white"
              backgroundColor={colors.aurora[400]}
              isLoading={isQueryStringSaveLoading}
              onClick={async () => {
                await changeQueryString({
                  newQueryString: queryStringState,
                  queryInputDateForColumnUpdate:
                    selectedInputDate ?? commonDate.currentDateStr(),
                });
                queryStringEditModeDisclosure.onClose();
              }}
            >
              Save & Preview Query
            </Button>
            <LenderAthenaQueryPreviewDrawer
              isOpen={isPreviewQueryResultsDrawerOpen}
              onClose={onClosePreviewQueryResultsDrawer}
              queryDataItems={queryDataItems}
              athenaQuery={recurringQuery}
              queryType="multiQuery"
              inputDate={inputDate}
              inputDateType={inputDateType}
            />
          </Flex>
        )}
      </Box>
    </Box>
  );
};
